import axios from 'axios'
import { getTimezoneHeaders } from '@/utils/timezoneUtils'

// API configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api'
const API_TIMEOUT = 30000

class ApiService {
  constructor() {
    // Create axios instance
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        // Add timestamp to prevent caching
        config.params = {
          ...config.params,
          _t: Date.now()
        }
        
        // Add stronger cache-busting for critical subscription-related endpoints
        const criticalEndpoints = [
          '/auth/subscription-status',
          '/auth/verify',
          '/dashboard',
          '/transactions',
          '/products',
          '/customers'
        ]
        
        const isCriticalEndpoint = criticalEndpoints.some(endpoint => 
          config.url && config.url.includes(endpoint)
        )
        
        if (isCriticalEndpoint) {
          config.headers = {
            ...config.headers,
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        }
        
        // Add timezone headers for proper server-side date handling
        config.headers = {
          ...config.headers,
          ...getTimezoneHeaders()
        }
        

        
        return config
      },
      (error) => {
        console.error('Request interceptor error:', error)
        return Promise.reject(error)
      }
    )

    // Response interceptor
    this.api.interceptors.response.use(
      (response) => {

        
        // Return data directly
        return response.data
      },
      (error) => {
        // Handle common errors
        this.handleError(error)
        return Promise.reject(error)
      }
    )
  }

  // Set authentication token
  setAuthToken(token) {
    if (token) {
      this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`
    } else {
      delete this.api.defaults.headers.common['Authorization']
    }
  }

  // Clear authentication token
  clearAuthToken() {
    delete this.api.defaults.headers.common['Authorization']
  }

  // Error handler
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const status = error.response.status
      const data = error.response.data
      
      console.error(`API Error ${status}:`, data)
      
      // Handle specific status codes
      switch (status) {
        case 401:
          // Unauthorized - redirect to login
          if (window.location.pathname !== '/login') {
            this.clearAuthToken()
            localStorage.clear()
            window.location.href = '/login'
          }
          break
        case 402:
        case 403:
          // Payment Required or Forbidden - could be subscription expired
          console.error('Access denied or subscription expired')
          
          // Check if it's a subscription expiry error
          if (data?.subscription_status === 'expired' || data?.subscription_status === 'deactivated') {
            // Emit subscription expired event
            window.dispatchEvent(new CustomEvent('subscription-expired', {
              detail: {
                message: data.message,
                subscription_status: data.subscription_status,
                subscription_end: data.subscription_end,
                days_remaining: data.days_remaining
              }
            }))
            
            // Force redirect to subscription page
            if (window.location.pathname !== '/subscription') {
              window.location.href = '/subscription'
            }
          }
          
          // Check if it's a subscription suspension error
          if (data?.subscription_status === 'suspended') {
            // Emit subscription suspended event
            window.dispatchEvent(new CustomEvent('subscription-suspended', {
              detail: {
                message: data.message,
                subscription_status: data.subscription_status,
                suspension_type: data.suspension_type,
                suspension_reason: data.suspension_reason,
                suspended_at: data.suspended_at,
                contact_support: data.contact_support
              }
            }))
            
            // Force redirect to suspension page (or show modal)
            if (window.location.pathname !== '/suspended') {
              window.location.href = '/suspended'
            }
          }
          break
        case 404:
          // Not found
          console.error('Resource not found')
          break
        case 500:
          // Server error
          console.error('Server error')
          break
        default:
          console.error('Unknown error')
      }
    } else if (error.request) {
      // Network error
      console.error('Network error:', error.request)
      
      // Check if offline
      if (!navigator.onLine) {
        console.error('Device is offline')
      }
    } else {
      // Request setup error
      console.error('Request error:', error.message)
    }
  }

  // HTTP methods
  async get(url, params = {}) {
    return this.api.get(url, { params })
  }

  async post(url, data = {}) {
    return this.api.post(url, data)
  }

  async put(url, data = {}) {
    return this.api.put(url, data)
  }

  async delete(url) {
    return this.api.delete(url)
  }

  async patch(url, data = {}) {
    return this.api.patch(url, data)
  }

  // Upload file
  async uploadFile(url, file, onProgress = null) {
    const formData = new FormData()
    formData.append('file', file)

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }

    if (onProgress) {
      config.onUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        )
        onProgress(percentCompleted)
      }
    }

    return this.api.post(url, formData, config)
  }

  // Batch requests
  async batch(requests) {
    try {
      const promises = requests.map(request => {
        const { method, url, data, params } = request
        switch (method.toLowerCase()) {
          case 'get':
            return this.get(url, params)
          case 'post':
            return this.post(url, data)
          case 'put':
            return this.put(url, data)
          case 'delete':
            return this.delete(url)
          case 'patch':
            return this.patch(url, data)
          default:
            throw new Error(`Unsupported method: ${method}`)
        }
      })

      return Promise.allSettled(promises)
    } catch (error) {
      console.error('Batch request error:', error)
      throw error
    }
  }

  // Reports API methods
  async getSalesReport(period = 'today') {
    try {
      return await this.get(`/transactions/reports/sales?period=${period}`)
    } catch (error) {
      console.error('Sales report error:', error)
      throw error
    }
  }

  async getGSTSalesSummary(fromDate, toDate) {
    try {
      const params = new URLSearchParams({
        from_date: fromDate,
        to_date: toDate
      })
      return await this.get(`/gst/sales-summary?${params}`)
    } catch (error) {
      console.error('GST sales summary error:', error)
      throw error
    }
  }

  async getDailySales(period = 'week') {
    try {
      return await this.get(`/transactions?period=${period}&group_by=day`)
    } catch (error) {
      console.error('Daily sales error:', error)
      throw error
    }
  }

  // Health check
  async healthCheck() {
    try {
      return await this.get('/health')
    } catch (error) {
      console.error('Health check failed:', error)
      return { success: false, error }
    }
  }

  // Subscription status check (cache-busted)
  async checkSubscriptionStatus() {
    try {
      return await this.get('/auth/subscription-status')
    } catch (error) {
      // Don't log 403 errors as they're expected for suspended/expired subscriptions
      if (error.response?.status !== 403) {
        console.error('Subscription status check failed:', error)
      }
      throw error
    }
  }

  // Clear all cached data related to subscription
  clearSubscriptionCache() {
    // Clear localStorage items that might contain cached subscription data
    const itemsToRemove = [
      'pos_business',
      'pos_user',
      'suspension_data',
      'subscription_status',
      'business_context'
    ]
    
    itemsToRemove.forEach(item => {
      localStorage.removeItem(item)
    })

  }
}

// Create and export singleton instance
export const apiService = new ApiService()

// Export class for testing
export { ApiService }
